import type { AxiosInstance } from 'axios';

import type { catalog } from '@terra/contracts';

/** Homepage section a product can be assigned to (admin-controlled). */
export type HomepageSection =
  | 'POPULAR_PICKS'
  | 'BOTTOM_GRID_1'
  | 'BOTTOM_GRID_2'
  | 'BEST_SELLING'
  | 'FEATURED';

export const HOMEPAGE_SECTIONS: HomepageSection[] = [
  'POPULAR_PICKS',
  'BOTTOM_GRID_1',
  'BOTTOM_GRID_2',
  'BEST_SELLING',
  'FEATURED',
];

export interface ProductListItem {
  id: string;
  title: string;
  slug: string;
  brand: string | null;
  brandId: string | null;
  brandRef?: { id: string; name: string; slug: string } | null;
  status: string;
  isActive: boolean;
  /** Who hid it while `isActive` is false: SELLER or ADMIN. */
  hiddenBy?: 'SELLER' | 'ADMIN' | null;
  isFeatured: boolean;
  /** Admin-assigned section. null when unassigned. */
  homepageSection: HomepageSection | null;
  /** Gate flag — admin must enable for the placement to render. */
  showOnHomepage: boolean;
  /** Higher = earlier in section ordering. */
  homepagePriority: number;
  /** Vendor's request; admin reviews on approval. */
  requestedHomepageSection: HomepageSection | null;
  /** Visual card badge (e.g. 'BESTSELLER'). 'none' / null = no badge. */
  productBadge: string | null;
  deletedAt: string | null;
  /** Who deleted it: SELLER (may restore) or ADMIN. */
  deletedBy?: 'SELLER' | 'ADMIN' | null;
  gstRate: number;
  createdAt: string;
  category: { id: string; name: string; slug: string };
  vendor: { id: string; storeName: string; slug: string };
  variants: Array<{
    id: string;
    sku: string;
    pricePaise: number;
    mrpPaise: number;
    optionValues: Record<string, string>;
    /** What a shopper pays now: `pricePaise` less the best live offer. */
    effectivePricePaise?: number;
    discountApplied?: boolean;
    /** The live offer's name, when one applies. */
    offerName?: string | null;
  }>;
  media: Array<{
    id: string;
    url: string;
    alt: string | null;
    publicId: string | null;
    isPrimary: boolean;
  }>;
  _count: { reviews: number };
  /**
   * Admin list only: whether shoppers can see it on the storefront, and if
   * not, why (disabled brand/category, suspended seller, not approved…).
   */
  storefront?: { visible: boolean; blockers: string[] };
}

export interface ProductDetail extends ProductListItem {
  /** Days the buyer has to return it after delivery; 0 = not returnable. */
  returnWindowDays?: number;
  description: string | null;
  bulletPoints: string[];
  hsnSac: string;
  attributes: Record<string, string>;
  reviews?: Array<{
    id: string;
    rating: number;
    title: string | null;
    body: string | null;
    customer: { id: string; name: string };
    createdAt: string;
  }>;
}

export interface ProductListResponse {
  data: ProductListItem[];
  page: { page: number; pageSize: number; total: number; pageCount: number };
}

/** One row in the header search typeahead. */
export interface SearchSuggestionProduct {
  id: string;
  title: string;
  slug: string;
  brand: string | null;
  category: { id: string; name: string; slug: string };
  /** Cheapest active variant; null when the product has no active variant. */
  pricePaise: number | null;
  mrpPaise: number | null;
  imageUrl: string | null;
  imageAlt: string | null;
}

/** A category whose name matched the typed term, with its sub-tree preview. */
export interface SearchSuggestionCategory {
  id: string;
  name: string;
  slug: string;
  parentId: string | null;
  parentName: string | null;
  children: Array<{ id: string; name: string; slug: string; productCount: number }>;
  /** A few products from this category or any of its descendants. */
  products: SearchSuggestionProduct[];
}

export interface SearchSuggestResponse {
  categories: SearchSuggestionCategory[];
  products: SearchSuggestionProduct[];
}

export function productEndpoints(http: AxiosInstance) {
  return {
    // storefront
    catalog: (params?: {
      page?: number;
      pageSize?: number;
      q?: string;
      categoryId?: string;
      /** Restrict to one vendor's storefront. */
      vendorId?: string;
      minPrice?: number;
      maxPrice?: number;
      sortBy?: string;
      sortDir?: string;
      featured?: boolean;
      /** When set, returns only products admin-assigned to this homepage section
       *  and gated `showOnHomepage = true`. Ordered by homepagePriority DESC. */
      homepageSection?: HomepageSection;
    }) => http.get<ProductListResponse>('/catalog', { params }).then((r) => r.data),

    getBySlug: (slug: string) => http.get<ProductDetail>(`/catalog/${slug}`).then((r) => r.data),
    /**
     * Storefront-visible product count per category id, each counting its whole
     * subtree. Categories with no live products are absent (treat as 0).
     */
    categoryCounts: () =>
      http
        .get<{ data: Record<string, number> }>('/catalog/category-counts')
        .then((r) => r.data.data),
    /** Header typeahead: matching categories (+ sub-categories + products) and products. */
    suggest: (q: string, signal?: AbortSignal) =>
      http
        .get<SearchSuggestResponse>('/catalog/suggest', {
          params: { q },
          ...(signal ? { signal } : {}),
        })
        .then((r) => r.data),

    /**
     * The signed-in customer's wishlist, as catalogue cards.
     *
     * Same shape as a catalogue page, deliberately: a wishlisted product must
     * render identically to the same product on a listing, prices included.
     * Items that have since been delisted do not come back.
     */
    getWishlist: (params?: { page?: number; pageSize?: number }) =>
      http.get<ProductListResponse>('/me/wishlist', { params }).then((r) => r.data),

    addToWishlist: (productId: string) =>
      http.post<void>(`/catalog/products/${productId}/wishlist`).then((r) => r.data),

    removeFromWishlist: (productId: string) =>
      http.delete<void>(`/catalog/products/${productId}/wishlist`).then((r) => r.data),

    // vendor
    myProducts: (params?: {
      page?: number;
      pageSize?: number;
      status?: string;
      q?: string;
      /** `deleted`: the seller's deleted products, to restore. */
      view?: 'deleted';
    }) => http.get<ProductListResponse>('/vendor/products', { params }).then((r) => r.data),
    /** Hide a listing from the storefront, or show it again (only if the seller hid it). */
    vendorSetVisibility: (productId: string, isActive: boolean) =>
      http
        .patch<{ id: string; isActive: boolean }>(`/vendor/products/${productId}/visibility`, {
          isActive,
        })
        .then((r) => r.data),
    /** Soft-delete a listing; it can be restored from the Deleted tab. */
    vendorDelete: (productId: string) =>
      http.delete<{ id: string }>(`/vendor/products/${productId}`).then((r) => r.data),
    vendorRestore: (productId: string) =>
      http.post<{ id: string }>(`/vendor/products/${productId}/restore`).then((r) => r.data),
    /** One of the seller's own products by id or slug, in any status — for the edit form. */
    myProduct: (idOrSlug: string) =>
      http
        .get<ProductDetail>(`/vendor/products/${encodeURIComponent(idOrSlug)}`)
        .then((r) => r.data),

    getVendorInventory: () => http.get<any>('/vendor/inventory').then((r) => r.data),

    adjustStock: (body: {
      variantId: string;
      warehouseId: string;
      delta: number;
      reason: string;
    }) => http.post<{ success: boolean }>('/vendor/inventory/adjust', body).then((r) => r.data),

    /** Seller — create many products from a sheet; `dryRun` checks every row and writes nothing. */
    bulkCreateProducts: (body: {
      dryRun: boolean;
      submitForApproval?: boolean;
      rows: catalog.BulkProductRow[];
    }) => http.post<catalog.BulkProductResponse>('/vendor/products/bulk', body).then((r) => r.data),

    /** Seller — set stock levels (quantity on hand now) for many SKUs from a sheet. */
    bulkSetStock: (body: { dryRun: boolean; reason?: string; rows: catalog.BulkStockRow[] }) =>
      http.post<catalog.BulkStockResponse>('/vendor/inventory/bulk', body).then((r) => r.data),

    /** Paginated stock-ledger entries for the calling vendor. */
    getVendorStockLedger: (params?: { page?: number; pageSize?: number; variantId?: string }) =>
      http
        .get<{
          data: Array<{
            id: string;
            variantId: string;
            delta: number;
            reason: string;
            refType: string | null;
            refId: string | null;
            createdAt: string;
            variant: {
              id: string;
              sku: string;
              optionValues: Record<string, string>;
              product: { id: string; title: string; slug: string };
            };
          }>;
          page: { page: number; pageSize: number; total: number; pageCount: number };
        }>('/vendor/inventory/history', { params })
        .then((r) => r.data),

    createProduct: (body: Record<string, unknown>) =>
      http.post<ProductDetail>('/vendor/products', body).then((r) => r.data),

    updateProduct: (productId: string, body: Record<string, unknown>) =>
      http.patch<ProductDetail>(`/vendor/products/${productId}`, body).then((r) => r.data),

    updateVariant: (
      variantId: string,
      body: {
        optionValues?: Record<string, string>;
        mrpPaise?: number;
        pricePaise?: number;
        weightGrams?: number;
        /** Packed box size, millimetres. */
        lengthMm?: number;
        widthMm?: number;
        heightMm?: number;
        barcode?: string;
        isActive?: boolean;
      },
    ) => http.patch<{ id: string }>(`/vendor/variants/${variantId}`, body).then((r) => r.data),

    deleteVariant: (variantId: string) =>
      http.delete<{ id: string }>(`/vendor/variants/${variantId}`).then((r) => r.data),

    submitForApproval: (productId: string) =>
      http
        .post<{ id: string; status: string }>(`/vendor/products/${productId}/submit`)
        .then((r) => r.data),

    // vendor product media (Cloudinary-backed gallery)
    addMedia: (
      productId: string,
      body: { fileId: string; url: string; publicId?: string; alt?: string; isPrimary?: boolean },
    ) => http.post<{ id: string }>(`/vendor/products/${productId}/media`, body).then((r) => r.data),

    setPrimaryMedia: (mediaId: string) =>
      http
        .patch<{ id: string; isPrimary: boolean }>(`/vendor/media/${mediaId}/primary`)
        .then((r) => r.data),

    removeMedia: (mediaId: string) =>
      http.delete<void>(`/vendor/media/${mediaId}`).then((r) => r.data),

    // admin
    listAll: (params?: {
      page?: number;
      pageSize?: number;
      q?: string;
      status?: string;
      categoryId?: string;
      vendorId?: string;
      isFirstParty?: 'true' | 'false';
      productKind?: 'PHYSICAL' | 'DIGITAL';
    }) => http.get<ProductListResponse>('/admin/products', { params }).then((r) => r.data),

    getById: (productId: string) =>
      http.get<ProductDetail>(`/admin/products/${productId}`).then((r) => r.data),

    approve: (productId: string, body?: { note?: string }) =>
      http
        .post<{ id: string; status: string }>(`/admin/products/${productId}/approve`, body ?? {})
        .then((r) => r.data),

    reject: (productId: string, body?: { note?: string }) =>
      http
        .post<{ id: string; status: string }>(`/admin/products/${productId}/reject`, body ?? {})
        .then((r) => r.data),

    requestChanges: (productId: string, body: { note: string }) =>
      http
        .post<{ id: string; status: string }>(`/admin/products/${productId}/request-changes`, body)
        .then((r) => r.data),

    // admin storefront controls
    setVisibility: (productId: string, isActive: boolean) =>
      http
        .patch<{
          id: string;
          isActive: boolean;
        }>(`/admin/products/${productId}/visibility`, { isActive })
        .then((r) => r.data),

    setFeatured: (productId: string, isFeatured: boolean) =>
      http
        .patch<{
          id: string;
          isFeatured: boolean;
        }>(`/admin/products/${productId}/feature`, { isFeatured })
        .then((r) => r.data),

    remove: (productId: string) =>
      http
        .delete<{ id: string; deletedAt: string | null }>(`/admin/products/${productId}`)
        .then((r) => r.data),

    restore: (productId: string) =>
      http
        .post<{ id: string; deletedAt: string | null }>(`/admin/products/${productId}/restore`)
        .then((r) => r.data),

    createProductAdmin: (vendorId: string, body: Record<string, unknown>) =>
      http.post<ProductDetail>(`/admin/products/vendor/${vendorId}`, body).then((r) => r.data),

    addMediaAdmin: (
      productId: string,
      vendorId: string,
      body: { fileId: string; url: string; publicId?: string; alt?: string; isPrimary?: boolean },
    ) =>
      http
        .post<{ id: string }>(`/admin/products/${productId}/vendor/${vendorId}/media`, body)
        .then((r) => r.data),

    /**
     * Admin sets homepage placement. Pass only the fields you want to change;
     * the rest are left untouched. `homepageSection: null` unassigns.
     */
    setPlacement: (
      productId: string,
      patch: {
        homepageSection?: HomepageSection | null;
        showOnHomepage?: boolean;
        homepagePriority?: number;
        productBadge?: string | null;
      },
    ) =>
      http
        .patch<{
          id: string;
          homepageSection: HomepageSection | null;
          showOnHomepage: boolean;
          homepagePriority: number;
          productBadge: string | null;
        }>(`/admin/products/${productId}/placement`, patch)
        .then((r) => r.data),
  };
}

export type ProductEndpoints = ReturnType<typeof productEndpoints>;
